import { useHistory } from 'react-router-dom'
import { assign } from 'xstate/lib/actions'

import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import { useSetSessionDetails } from '@flowwai-frontend/shared/auth/common'
import { useReportBug } from '@flowwai-frontend/shared/common'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

import loginMachine from '../machines/LoginMachine'
import { getPath } from '../utils/StorageUtils'
import { convertSecondsToDays } from '../utils/timeUtils'
import {
   LoginWithEmailRequestType,
   LoginWithUsernameRequestType
} from '../services/types'

import { useLoginWithEmailAPI } from './apis/useLoginWithEmailAPI'
import { useLoginWithUsernameAPI } from './apis/useLoginWithUsernameAPI'
import { useResendEmailOTPAPI } from './apis/useResendEmailOTPAPI'
import { useResendUsernameOTPAPI } from './apis/useResendUsernameOTPAPI'
import { useVerifyEmailOTPAPI } from './apis/useVerifyEmailOTPAPI'
import { useVerifyUsernameOTPAPI } from './apis/useVerifyUsernameOTPAPI'

const useLoginMachine = ({
   timerRef
}: {
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
}) => {
   const { loginWithEmailAPI } = useLoginWithEmailAPI()
   const { verifyEmailOTPAPI } = useVerifyEmailOTPAPI()
   const { resendEmailOTPAPI } = useResendEmailOTPAPI()

   const { loginWithUsernameAPI } = useLoginWithUsernameAPI()
   const { verifyUsernameOTPAPI } = useVerifyUsernameOTPAPI()
   const { resendUsernameOTPAPI } = useResendUsernameOTPAPI()

   const t = useCustomT('auth:errorMessages')

   const setSessionDetails = useSetSessionDetails()
   const reportBug = useReportBug()

   const history = useHistory()

   const loginMachineWithConfig = loginMachine().withConfig({
      actions: {
         assignErrorMessageToContext: assign((_, event: any) => {
            const errorMessage = event.data?.response || t('unknownError')

            return {
               errorMessage: errorMessage
            }
         }),
         clearErrorMessageInContext: assign(_ => ({
            errorMessage: ''
         })),
         assignEmailToContext: assign((_, event: any) => ({
            email: event.email
         })),
         assignUsernameToContext: assign((_, event: any) => ({
            username: event.username
         })),
         updateEmailAndMaskedPhoneNumberInContext: assign((_, event: any) => {
            const { email, masked_phone_number } = event.data

            return {
               email: email,
               maskedPhoneNumber: masked_phone_number
            }
         }),
         updateUsernameAndEmailAndMaskedPhoneNumberInContext: assign(
            (_, event: any) => {
               const {
                  masked_email,
                  username,
                  masked_phone_number
               } = event.data

               return {
                  email: masked_email,
                  username,
                  maskedPhoneNumber: masked_phone_number
               }
            }
         ),
         navigateSuccessPage: _ => {
            const path = getPath()
            history.push(path)
         },
         assignOtpToContext: assign((_, event: any) => ({ otp: event.otp })),

         onLoginSuccess: (context, event: any) => {
            reportBug?.addCustomData('Email', context.email)

            const sessionDetails = {
               accessToken: event.data.access_token,
               refreshToken: event.data.refresh_token,
               expiredInDays: convertSecondsToDays(
                  event.data.expires_in_seconds
               )
            }

            setSessionDetails(sessionDetails)
         },
         onLoginWithUsernameSuccess: (context, event: any) => {
            reportBug?.addCustomData('Email', context.email)
            reportBug?.addCustomData('Username', context.username)

            const sessionDetails = {
               accessToken: event.data.access_token,
               refreshToken: event.data.refresh_token,
               expiredInDays: convertSecondsToDays(
                  event.data.expires_in_seconds
               )
            }

            setSessionDetails(sessionDetails)
            window.location.reload()
         },
         startTimer: () => {
            timerRef.current?.start()
         }
      },
      services: {
         loginWithEmailOtp: (context, args: any) => {
            const isTncDefined =
               args.isAcceptedTnc !== undefined && args.isAcceptedTnc !== null

            const isCaptchaTokenAvailable =
               args.captchaToken !== undefined && args.captchaToken !== null

            const requestObject: LoginWithEmailRequestType = {
               email: context.email,
               ...(isTncDefined && {
                  is_accepted_tnc: args.isAcceptedTnc ? 'YES' : 'NO'
               }),
               ...(isCaptchaTokenAvailable && {
                  captcha_token: args.captchaToken
               })
            }

            return loginWithEmailAPI(requestObject)
         },
         verifyEmailOtp: context =>
            verifyEmailOTPAPI({
               otp_value: context.otp,
               email: context.email,
               client_id: Config.LOGIN_CLIENT_ID
            }),
         resendEmailOtp: context => resendEmailOTPAPI({ email: context.email }),
         loginWithUsernameOtp: (context, args: any) => {
            const isTncDefined =
               args.isAcceptedTnc !== undefined && args.isAcceptedTnc !== null

            const isCaptchaTokenAvailable =
               args.captchaToken !== undefined && args.captchaToken !== null

            const requestObject: LoginWithUsernameRequestType = {
               username: context.username,
               ...(isTncDefined && {
                  is_accepted_tnc: args.isAcceptedTnc ? 'YES' : 'NO'
               }),
               ...(isCaptchaTokenAvailable && {
                  captcha_token: args.captchaToken
               })
            }

            return loginWithUsernameAPI(requestObject)
         },
         verifyUsernameOtp: context =>
            verifyUsernameOTPAPI({
               otp_value: context.otp,
               username: context.username,
               client_id: Config.LOGIN_CLIENT_ID
            }),
         resendUsernameOtp: context =>
            resendUsernameOTPAPI({ username: context.username })
      }
   })

   return { loginMachine: loginMachineWithConfig }
}

export default useLoginMachine
